import Two from 'two.js';
import { Group as TweenGroup } from '@tweenjs/tween.js';
import type { RefObject } from 'react';
import { Group } from 'two.js/src/group';
import { Rectangle } from 'two.js/src/shapes/rectangle';
import { Text } from 'two.js/src/text';
import { drawFpsCounter } from 'rf-ui/GameRenderer/rendererHelper';

const debug = {
  showEdge: false
};

export default abstract class TwoScene {
  two: Two;

  domElement: RefObject<HTMLDivElement | null>;

  scene: Group | null = null;

  sceneWidth: number = 750;

  sceneHeight: number = 750;

  debugScaleRect: any = null;

  debugEdgeRect: Rectangle | null = null;

  tweens: TweenGroup = new TweenGroup();

  fpsCountText: Text | null = null;

  fpsCount: number[] = [];

  bodyStyle: CSSStyleDeclaration | null = null;

  timeouts: Array<ReturnType<typeof setTimeout>> = [];

  constructor(domElement: RefObject<HTMLDivElement | null>) {
    this.domElement = domElement;
    this.bodyStyle = document.body.style;
    this.two = new Two({
      fullscreen: true,
      fitted: false,
      autostart: true,
      type: Two.Types.svg
    }).appendTo(this.domElement.current!);

    this.two.bind('update', this.update);
    this.two.bind('resize', this.onResize);
  }

  abstract setupScene(): Group;

  setup() {
    this.scene = this.setupScene();
    this.scene.translation.set(this.two!.width - this.sceneWidth / 2, this.two!.height - this.sceneHeight / 2);
    const hashQuery = window.location.hash.includes('?')
      ? window.location.hash.slice(window.location.hash.indexOf('?'))
      : '';
    const params = new URLSearchParams(window.location.search || hashQuery);
    const fpsParam = params.get('fps');
    const showFPS = params.has('fps') && (fpsParam === null || fpsParam === '' || fpsParam === '1' || fpsParam === 'true');
    if (debug.showEdge) this.setupDebugRects();
    if (showFPS) {
      this.fpsCountText = drawFpsCounter(this.two!);
    }
    this.updateScaling();
  }

  setupDebugRects() {
    this.debugEdgeRect = this.two!.makeRectangle(this.two!.width / 2, this.two!.height / 2, this.two!.width, this.two!.height);
    this.debugEdgeRect.noFill();
    this.debugEdgeRect.stroke = 'red';
    this.debugEdgeRect.linewidth = 5;

    this.debugScaleRect = this.two!.makeRectangle(this.scene!.translation.x, this.scene!.translation.y, this.sceneWidth, this.sceneHeight)
      .noFill();
    this.debugScaleRect.stroke = 'yellow';
    this.debugScaleRect.linewidth = 3;
  }

  updateScaling() {
    if (!this.scene) return;

    const container = document.getElementById('wheelContainer')!;
    const containerW = parseInt(container.offsetWidth as any);
    const containerH = window.innerHeight - parseInt(container.offsetTop as any);
    const lowest = Math.min(containerH, containerW);

    this.scene.scale = lowest / 750;
    const leftoverWidth = containerW - this.sceneWidth * this.scene.scale;
    const leftoverHeight = containerH - this.sceneHeight * this.scene.scale;
    this.scene.translation
      .set(this.two!.width - leftoverWidth / 2 - (this.sceneWidth * this.scene.scale) / 2, this.two!.height - leftoverHeight / 2 - (this.sceneHeight * this.scene.scale) / 2);

    if (this.debugScaleRect) {
      this.debugScaleRect.translation.set(this.scene.translation.x, this.scene.translation.y);
      this.debugScaleRect.width = this.sceneWidth * this.scene.scale;
      this.debugScaleRect.height = this.sceneHeight * this.scene.scale;
    }
  }

  updateDebugEdgeRect() {
    if (this.debugEdgeRect) {
      this.debugEdgeRect.translation.set(this.two!.width / 2, this.two!.height / 2);
      this.debugEdgeRect.width = this.two!.width;
      this.debugEdgeRect.height = this.two!.height;
    }
  }

  onResize = () => {
    this.updateScaling();
    this.updateDebugEdgeRect();
  };

  update = () => {
    if (this.fpsCountText) {
      this.updateFPS();
    }
    this.tweens.update();
  };

  updateFPS() {
    const now = Date.now();
    const oneSecondAgo = now - 1000;

    this.fpsCount = this.fpsCount.filter(entry => entry >= oneSecondAgo);
    this.fpsCount.push(now);
    const fps = this.fpsCount.length;

    if (this.fpsCountText) {
      this.fpsCountText.value = `${fps} fps`;
    }
  }

  unmountTwo() {
    if (this.two) {
      this.two.unbind('update');
      this.two.unbind('resize');
      this.two.pause();
      this.two.release();
      this.two.clear();
      this.domElement?.current?.removeChild(this.two.renderer.domElement);
    }
    this.tweens.removeAll();
    this.debugEdgeRect = null;
    this.debugScaleRect = null;
  }

  unmount = () => {
    if (this.bodyStyle) {
      (document.body.style as any) = this.bodyStyle;
    }
    this.timeouts.forEach(t => {
      clearTimeout(t);
    });
    this.unmountTwo();
  };
}
